import axios from 'frontend/_config/axios'
import setupRouter from 'frontend/_setup/setupRouter/setupRouter'
import store from 'frontend/_stores'
import { POST, ResourceService } from 'frontend/common/services/resource-service'
import { SimpleSelectService } from 'frontend/common/services/simple-select-service'
import { ValidationService } from 'frontend/common/services/validation-service'
import { startCase } from 'lodash'
import pluralize from 'pluralize/pluralize'
import { getFileNameFromContentDisposition } from "frontend/common/files/functions/getFileNameFromContentDisposition";
import { downloadFileFromBlob } from "frontend/common/files/functions/downloadFileFromBlob";

export class BusModuleSubsystemsService extends ResourceService {
  declare baseUrl: string
  declare fetchError: string
  declare saveError: string
  declare deleteError: string
  declare validationService: ValidationService
  declare selectService: SimpleSelectService

  constructor({
    apiProvider = axios,
    eventSlug = setupRouter.currentRoute.value.params.eventSlug,
    baseUrl = `/events/${eventSlug}/bus_module_subsystems`,
    blocker = null,
    blockerKey = 'default',
    fetchError = 'Failed to fetch bus module subsystems.',
    saveError = 'Failed to save bus module subsystem.',
    deleteError = 'Failed to delete bus module subsystem.',
    selectService = new SimpleSelectService(baseUrl, {
      apiProvider,
      blocker,
      blockerKey: 'bus_module_subsystems',
    }),
  } = {}) {
    super({ apiProvider, baseUrl, blocker, fetchError, saveError, deleteError })
    this.baseUrl = baseUrl
    this.blocker = blocker
    this.blockerKey = blockerKey
    this.fetchError = fetchError
    this.saveError = saveError
    this.deleteError = deleteError
    this.validationService =
      this.validationService ?? new ValidationService({ apiProvider, baseUrl, blocker })
    this.selectService = selectService
  }

  getForSelect() {
    return (additionalPayload = {}) => {
      const path = `${this.baseUrl}/selection_index`
      const fields = ['id', 'name']


      return this.selectService.fetchOptions({
        path,
        additionalPayload,
        fields,
      })
    }
  }

  prepareMessageFromErrors(errors, delimiter = '<br>') {
    const result = []
    Object.keys(errors).forEach(k => {
      result.push(`${startCase(k.replace('Id', ''))}: ${errors[k].join(', ')}`)
    })
    return result.join(delimiter)
  }

  dispatchAction(
    action,
    id,
    { data = {}, url = `${this.baseUrl}${id ? '/' + id : ''}/${action}` } = {},
  ) {
    const errorHandler = error => {
      const messagePayload = { message: 'Requested action cannot be run' }
      messagePayload.message = error.response?.data?.message || messagePayload.message
      if (error.response?.status == 422 && error.response?.data?.errors?.base?.length) {
        messagePayload.title = messagePayload.message
        messagePayload.message =
          error.response.data.errors.base.constructor == Array
            ? error.response.data.errors.base.join('; ')
            : error.response.data.errors.base
      } else if (error.response?.status == 422 && error.response?.data?.errors) {
        messagePayload.title = messagePayload.message
        messagePayload.message = this.prepareMessageFromErrors(error.response.data.errors)
      }
      store.dispatch('toast/error', messagePayload)
    }
    return this._makeCall(POST, errorHandler, { url, data })
  }

  allocateShuttleTransfers(subsystemId: string, shuttleTransferIds: string[], startAt: DateIsoString, endAt: DateIsoString,  errorHandler = err => this._onFetchError(err), config = {}) {
    const url = `${this.baseUrl}/${subsystemId}/allocate_shuttle_transfers`
    const data = {
      allocation: {
        shuttleTransferIds,
        startAt,
        endAt,
      },
    }
    return this._makeCall(POST, errorHandler, { url, data, ...config })
  }

  async generateShiftJobCards(
    subsystemIds,
    { config = {}, errorHandler = e => this._onSaveError(e) } = {},
  ) {
    const payload = { subsystemIds: subsystemIds }
    this._block()

    try {
      const response = await this.apiProvider.post(
        `${this.baseUrl}/batch_generate_shift_job_cards`,
        payload,
        {
          ...config,
          responseType: 'blob',
        },
      )
      const fileName = getFileNameFromContentDisposition(response.headers['content-disposition'])
      downloadFileFromBlob(response.data, fileName)
    } catch (error) {
      errorHandler(error, this.saveError)
      throw error
    } finally {
      this._unblock()
    }
  }

}
